/**
 * Populates the cart shipping rates modal from section block data.
 */
class CartShippingRates extends HTMLElement {
  /** @type {Array<{country_code: string, country_name: string, method_name: string, price: string, delivery_note: string}>} */
  #rates = [];

  connectedCallback() {
    this.#rates = this.#parseRates();
    this.#buildCountrySelect();
    this.#bindSelect();
    this.#renderTable(this.#getDefaultCountry());
  }

  #parseRates() {
    const node = this.querySelector('[data-shipping-rates-json]');
    if (!node?.textContent) return [];

    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed.filter((row) => row.country_code) : [];
    } catch {
      return [];
    }
  }

  #getCountries() {
    const map = new Map();

    this.#rates.forEach((rate) => {
      const code = rate.country_code.toUpperCase();
      if (!map.has(code)) {
        map.set(code, rate.country_name || code);
      }
    });

    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }

  #getDefaultCountry() {
    const datasetCountry = document.documentElement.lang?.includes('-')
      ? document.documentElement.lang.split('-').pop()?.toUpperCase()
      : '';

    const countries = this.#getCountries();
    if (!countries.length) return '';

    const match = countries.find(([code]) => code === datasetCountry);
    return match?.[0] ?? countries[0][0];
  }

  #buildCountrySelect() {
    const select = this.querySelector('[data-shipping-country-select]');
    if (!(select instanceof HTMLSelectElement)) return;

    select.innerHTML = '';
    const countries = this.#getCountries();

    if (!countries.length) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = 'No shipping rates configured';
      select.appendChild(option);
      select.disabled = true;
      return;
    }

    countries.forEach(([code, name]) => {
      const option = document.createElement('option');
      option.value = code;
      option.textContent = name;
      select.appendChild(option);
    });

    select.value = this.#getDefaultCountry();
  }

  #bindSelect() {
    const select = this.querySelector('[data-shipping-country-select]');
    select?.addEventListener('change', () => {
      if (select instanceof HTMLSelectElement) {
        this.#renderTable(select.value);
      }
    });
  }

  /** @param {string} countryCode */
  #renderTable(countryCode) {
    const body = this.querySelector('[data-shipping-rates-body]');
    if (!body) return;

    body.innerHTML = '';
    const rows = this.#rates.filter((rate) => rate.country_code.toUpperCase() === countryCode.toUpperCase());

    if (!rows.length) {
      const tr = document.createElement('tr');
      tr.innerHTML = '<td colspan="3">No rates available for this country.</td>';
      body.appendChild(tr);
      return;
    }

    rows.forEach((rate) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${rate.method_name || 'Standard'}</td>
        <td>${rate.delivery_note || '—'}</td>
        <td><strong>${rate.price || '—'}</strong></td>
      `;
      body.appendChild(tr);
    });
  }
}

if (!customElements.get('cart-shipping-rates')) {
  customElements.define('cart-shipping-rates', CartShippingRates);
}
